/**
 * Furnace error mapping — pure-function module.
 *
 * Bridge-3 UX layer: when an `agent:stream` `'error'` chunk arrives
 * during a build that was routed to a Furnace, the renderer needs to
 * present the engineer with actionable copy + a recovery hint —
 * never a raw error message that leaks an infrastructure URL or a
 * stack trace.
 *
 * This module classifies a raw error message into one of the known
 * Furnace failure classes and returns a {@link FurnaceErrorView} with:
 *   - A short, user-facing title.
 *   - A recovery hint pointing the engineer to a sensible next step.
 *   - A `kind` discriminator the renderer can branch on to mount the
 *     appropriate primary CTA (`retry` vs `escalate` vs `check-cohort`).
 *
 * Privacy + safety invariants:
 *   - No infrastructure URL (`volley-services.net`, hostnames, etc.)
 *     is ever embedded in any returned title or hint.
 *   - No raw error message is returned verbatim — every classified
 *     case rewrites the copy. The `'unknown'` class falls back to a
 *     generic "Furnace build failed" with the raw text stashed only
 *     in the `diagnostic` field (which the renderer surfaces in the
 *     Diagnostics → Copy diagnostics blob, NOT in the toast copy).
 *   - The escalation pointer is always `#foundry` (the rollout
 *     owner's Slack channel) — never a direct email or pager URL.
 *
 * British spelling throughout.
 */

/**
 * Classified Furnace failure class. Mirrors the failure modes in
 * `docs/onboarding-furnace.md` § "Common failure modes":
 *
 *   - `'provisioner'` — the Furnace pod could not be provisioned
 *     (control-plane refused, no capacity, image pull failed). The
 *     engineer's recovery is to retry; persistent failure escalates.
 *   - `'policy-deny'` — a tool call hit the in-pod policy gate. The
 *     engineer's recovery is to read the deny copy in the chat and
 *     escalate if it's load-bearing.
 *   - `'timeout'` — the Furnace build ran past its wall-clock or
 *     budget ceiling. The engineer's recovery is to scope the
 *     request smaller.
 *   - `'network'` — the WSS connection dropped mid-build. The
 *     engineer's recovery is usually to wait for reconnect; if it
 *     doesn't clear, retry the turn.
 *   - `'oom'` — the pod ran out of memory. The engineer's recovery
 *     is to retry (fresh pod has a clean memory state); persistent
 *     OOM escalates.
 *   - `'auth'` — the pod refused the session handshake (cohort
 *     consult succeeded but auth surface couldn't mint a token).
 *     The engineer's recovery is to re-test the tokens in Settings →
 *     Connections and restart Foundry.
 *   - `'unknown'` — the error didn't match any known class. The
 *     engineer's recovery is to escalate with the diagnostics blob.
 */
export type FurnaceErrorKind =
  | 'provisioner'
  | 'policy-deny'
  | 'timeout'
  | 'network'
  | 'oom'
  | 'auth'
  | 'unknown';

/** Primary CTA the renderer mounts beside the error copy. */
export type FurnaceErrorPrimaryCta =
  /** Re-dispatch the failing turn. The renderer fires the same
   *  `agent:send` invoke with the same prompt. */
  | 'retry'
  /** Open Settings → Diagnostics (the user copies the blob and
   *  posts it in #foundry). */
  | 'open-diagnostics'
  /** Open the Furnace status panel (Settings → Advanced → Furnace
   *  status). The engineer checks their cohort + flag state. */
  | 'check-cohort'
  /** Open the Settings → Connections panel so the engineer can
   *  re-test the Anthropic + GitHub tokens. */
  | 'check-connections';

/**
 * The rendered view of a classified Furnace error. The renderer
 * paints `.title` + `.hint` in the chat error bubble and mounts the
 * `.primaryCta` button. The `.escalation` line is rendered as
 * secondary text below the hint.
 */
export interface FurnaceErrorView {
  /** Classified failure class. */
  kind: FurnaceErrorKind;
  /** Short, user-facing title. Renders as the headline of the
   *  error bubble. Free of infrastructure URLs by construction. */
  title: string;
  /** Plain-English recovery hint pointing the engineer to a
   *  sensible next step. Free of infrastructure URLs by
   *  construction. */
  hint: string;
  /** Always-on escalation pointer. Pinned by tests so a future copy
   *  edit can't drop it. */
  escalation: string;
  /** Primary CTA the renderer mounts beside the copy. */
  primaryCta: FurnaceErrorPrimaryCta;
  /** Raw error string from the chunk. Reserved for the Diagnostics
   *  blob; the renderer must NOT surface this in the toast copy. */
  diagnostic: string;
}

/** Always-rendered escalation pointer. */
const ESCALATION = 'Post in #foundry on Slack if it persists.';

/**
 * Classify a raw error message into a Furnace failure class.
 *
 * Pure function — no side effects, no infrastructure URL scrubbing
 * required because the classified copy never embeds the raw text.
 * The matching is case-insensitive + substring-based: we'd rather
 * over-match a class than fall through to `'unknown'` and lose the
 * actionable hint.
 *
 * Ordering matters: the more-specific patterns (`'oom'`,
 * `'timeout'`) are checked BEFORE the more-general ones
 * (`'provisioner'`, `'network'`) so a "build timed out after pod
 * unreachable" still classifies as `'timeout'`.
 */
export function classifyFurnaceError(rawErrorText: string): FurnaceErrorView {
  const text = rawErrorText.toLowerCase();
  const diagnostic = rawErrorText;

  // Order is intentional — see function-level comment.

  // Bridge-3 close-out (Bugbot Medium) — match `oom` on word
  // boundaries, not as a raw substring. The previous
  // `text.includes('oom')` matched "no room left on device" + "Zoom
  // API error" + any other word containing the trigram `oom`,
  // mis-classifying unrelated failures as OOM and showing the
  // memory CTA + banner. The widened Med #2 latch surface
  // exposed this — more error messages now reach the classifier.
  // Substring matches against `oomkilled` (Kubernetes-style) +
  // `out of memory` (long-form) plus a regex-boundary match for
  // bare `oom` cover the real failure modes without false
  // positives.
  if (text.includes('oomkilled') || text.includes('out of memory') || /\boom\b/.test(text)) {
    return {
      kind: 'oom',
      title: 'Furnace ran out of memory.',
      hint: 'Retry — a fresh pod has a clean memory state. If it OOMs again on the same request, scope the request smaller.',
      escalation: ESCALATION,
      primaryCta: 'retry',
      diagnostic,
    };
  }

  if (
    text.includes('timeout') ||
    text.includes('timed out') ||
    text.includes('deadline exceeded') ||
    text.includes('budget exceeded')
  ) {
    return {
      kind: 'timeout',
      title: 'Furnace build timed out.',
      hint: 'Try a smaller scope, or break the request into multiple turns. The per-message turn cap and budget are tunable in Settings → Advanced.',
      escalation: ESCALATION,
      primaryCta: 'retry',
      diagnostic,
    };
  }

  if (
    text.includes('policy') ||
    text.includes('denied') ||
    text.includes('pretooluse') ||
    text.includes('canusetool') ||
    text.includes('not allowed') ||
    text.includes('not permitted')
  ) {
    return {
      kind: 'policy-deny',
      title: 'Build hit a policy guard.',
      hint: 'A tool call was refused by the in-pod policy gate. Read the deny copy in the chat for the reason and an alternative path.',
      escalation: ESCALATION,
      primaryCta: 'open-diagnostics',
      diagnostic,
    };
  }

  // Bridge-3 close-out (Bugbot Medium round-2) — match `auth` and
  // `401` on word boundaries, not as raw substrings. The previous
  // `text.includes('auth')` mis-classified messages containing
  // "authority", "author", "authenticator"-prefixed identifiers,
  // etc.; `text.includes('401')` mis-classified numeric tokens
  // like "1401" or "8401" that happen to embed the HTTP code.
  // The whole-word `\b401\b` covers HTTP statuses, error codes,
  // and Slack thread IDs that are genuinely the auth code; the
  // long-form `unauthorized` / `unauthorised` / `token_expired`
  // substrings stay as-is because they're already unambiguous.
  // `\bauth\b` covers shell summaries like `Reason: auth` while
  // rejecting "authority" / "authenticator" — and the explicit
  // `authenticat` substring catches the "authentication failed"
  // / "authenticator refused" cases the bare word misses.
  if (
    /\bauth\b/.test(text) ||
    text.includes('authenticat') ||
    /\b401\b/.test(text) ||
    text.includes('token_expired') ||
    text.includes('unauthorized') ||
    text.includes('unauthorised')
  ) {
    return {
      kind: 'auth',
      title: 'Furnace refused the session.',
      hint: 'Re-test your Anthropic and GitHub tokens in Settings → Connections. If both pass, restart Foundry.',
      escalation: ESCALATION,
      primaryCta: 'check-connections',
      diagnostic,
    };
  }

  if (
    text.includes('provision') ||
    text.includes('capacity') ||
    text.includes('image pull') ||
    text.includes('crucible-706') ||
    text.includes('cloud-mode runtime is missing')
  ) {
    return {
      kind: 'provisioner',
      title: 'Could not provision Furnace workspace.',
      hint: 'Retry once. If it still fails, check that you are still in cohort (Settings → Advanced → Furnace status).',
      escalation: ESCALATION,
      primaryCta: 'check-cohort',
      diagnostic,
    };
  }

  if (
    text.includes('econnreset') ||
    text.includes('econnrefused') ||
    text.includes('socket') ||
    text.includes('network') ||
    text.includes('connection') ||
    text.includes('disconnect') ||
    text.includes('unreachable')
  ) {
    return {
      kind: 'network',
      title: 'Lost connection to Furnace.',
      hint: 'Reconnecting automatically. If the chrome stays red for more than a few seconds, retry the turn.',
      escalation: ESCALATION,
      primaryCta: 'retry',
      diagnostic,
    };
  }

  // Default — surface a generic title; the raw message stays in
  // the diagnostic stash for the engineer to copy out of
  // Diagnostics → Copy diagnostics.
  return {
    kind: 'unknown',
    title: 'Furnace build failed.',
    hint: 'Open Settings → Diagnostics and post the copied blob in #foundry. Retrying often works for transient failures.',
    escalation: ESCALATION,
    primaryCta: 'open-diagnostics',
    diagnostic,
  };
}

/**
 * Sentinel substrings that should NEVER appear in a user-facing
 * Furnace error title or hint. The chrome's snapshot test asserts
 * this for every classified case so a future copy edit can't leak
 * an infrastructure URL.
 *
 * Kept as a list (not a regex) so the test failure pinpoints which
 * exact substring leaked.
 */
export const FURNACE_USER_FACING_BANNED_SUBSTRINGS: readonly string[] = [
  'volley-services.net',
  'volley-internal',
  'staging.volley',
  'forge-dev',
  'crucible-registry-api',
  'crucible-anthropic-proxy',
  '.internal',
];

/**
 * Test-only superset: includes the HTTP/HTTPS prefixes too. The
 * runtime scrubber catches arbitrary URLs via a regex pass — the
 * regex MUST run before the substring pass because stripping
 * `http://` first leaves the hostname exposed. Banner / chrome
 * snapshot tests assert against this superset to catch both the
 * substring tokens AND any unscrubbed URL fragments.
 */
export const FURNACE_USER_FACING_BANNED_SUBSTRINGS_FOR_TESTS: readonly string[] = [
  ...FURNACE_USER_FACING_BANNED_SUBSTRINGS,
  'http://',
  'https://',
];

/**
 * Scrub infrastructure URLs from an arbitrary error message before
 * it reaches a user-facing surface (assistant chat bubble, toast,
 * etc.). Covers the Codex round-1 HIGH on Bridge-3: the chat
 * reducer's `error` branch stores `chunk.error` verbatim, which
 * `CrucibleErrorState` then renders in the assistant bubble. A
 * raw Furnace failure like `failed to dial
 * forge-dev.volley-services.net` would otherwise leak the
 * infrastructure URL into the chat surface even though the
 * `<FurnaceErrorBanner>` stays clean.
 *
 * Strategy:
 *   - Substring-strip every banned token (replace with `[redacted]`
 *     so the engineer sees there WAS a URL there but not the URL
 *     itself).
 *   - Belt-and-braces regex strip for `https?://...` patterns the
 *     banned-list might not catch (e.g. a hostname we forgot to
 *     enumerate).
 *
 * Pure function — no side effects. Safe to call on every error
 * chunk regardless of routing — non-Furnace errors typically don't
 * contain banned substrings, so the input passes through unchanged.
 */
/**
 * The sentinel prefix `unflattenIpcError` looks for. Duplicated here
 * to avoid an import cycle — the value MUST match
 * `shared/error-types.ts`'s `STRUCTURED_IPC_ERROR_SENTINEL`. A test
 * pins them equal so a future drift triggers loudly.
 */
const STRUCTURED_IPC_ERROR_SENTINEL = '[CRUCIBLE_ERR_V1]';

/**
 * Scrub level — controls whether the catch-all URL regex runs.
 *
 *   - `'banned-only'` — only strip the explicit banned infrastructure
 *     substrings. Non-Furnace error messages (local build mode,
 *     flag-OFF) that mention a public or localhost URL pass through
 *     unchanged. This is the SAFE-FOR-LOCAL default.
 *   - `'aggressive'` — also strip any `http://` / `https://` URL
 *     wholesale. Used ONLY when we know the error came from a
 *     Furnace-bound turn (Codex round-3 finding: pre-Bridge-3
 *     behaviour mustn't change for local-build errors that
 *     mention `http://localhost:3000` etc.).
 */
type ScrubLevel = 'banned-only' | 'aggressive';

function scrubPlainString(text: string, level: ScrubLevel = 'aggressive'): string {
  if (!text) return text;
  let scrubbed = text;
  // Pass 1 — URL regex, ONLY when in aggressive mode. The pattern
  // stops at whitespace AND at JSON string-terminators (`"`,
  // `\`, `,`, `}`, `]`) so a URL embedded inside a JSON payload
  // doesn't consume the closing quote (Codex round-2 finding).
  if (level === 'aggressive') {
    scrubbed = scrubbed.replace(/https?:\/\/[^\s"\\,}\]]+/gi, '[redacted-url]');
  }
  // Pass 2 — banned-substring scrub. Runs in both levels. Strips
  // bare hostnames that aren't URL-shaped (e.g.
  // `forge-dev.volley-services.net:443`).
  for (const banned of FURNACE_USER_FACING_BANNED_SUBSTRINGS) {
    const lower = scrubbed.toLowerCase();
    const idx = lower.indexOf(banned.toLowerCase());
    if (idx === -1) continue;
    const pattern = new RegExp(banned.replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&'), 'gi');
    scrubbed = scrubbed.replace(pattern, '[redacted]');
  }
  return scrubbed;
}

/**
 * User-facing string fields on a `SerialisedError` that the chat
 * bubble + diagnostics UI can render. Codex round-2 found that
 * scrubbing only `.message` left URLs reachable via `.recovery`,
 * `.processOutput.{stdoutTail,stderrTail,binary,argv,cwd}`,
 * `.systemError.{path,dest}`, and the recursive `.cause` chain.
 *
 * The recursive scrubber visits each of these fields and applies
 * `scrubPlainString` in place. Anything not in the list passes
 * through unchanged (`code`, `category`, `retryable`, `errno`,
 * `exitCode` — none of these are URL-shaped).
 */
function scrubSerialisedErrorInPlace(
  payload: Record<string, unknown>,
  depth: number,
  level: ScrubLevel,
): void {
  // Defensive depth bound — flattenIpcError already caps the
  // cause chain but the renderer should never hang on a
  // pathological encode anyway.
  if (depth > 8) return;

  if (typeof payload.message === 'string') {
    payload.message = scrubPlainString(payload.message, level);
  }
  if (typeof payload.recovery === 'string') {
    payload.recovery = scrubPlainString(payload.recovery, level);
  }

  // processOutput sub-tree.
  const po = payload.processOutput;
  if (po !== null && typeof po === 'object') {
    const proc = po as Record<string, unknown>;
    if (Array.isArray(proc.stdoutTail)) {
      proc.stdoutTail = (proc.stdoutTail as unknown[]).map((line) =>
        typeof line === 'string' ? scrubPlainString(line, level) : line,
      );
    }
    if (Array.isArray(proc.stderrTail)) {
      proc.stderrTail = (proc.stderrTail as unknown[]).map((line) =>
        typeof line === 'string' ? scrubPlainString(line, level) : line,
      );
    }
    if (typeof proc.binary === 'string') {
      proc.binary = scrubPlainString(proc.binary, level);
    }
    if (typeof proc.cwd === 'string') {
      proc.cwd = scrubPlainString(proc.cwd, level);
    }
    if (Array.isArray(proc.argv)) {
      proc.argv = (proc.argv as unknown[]).map((a) =>
        typeof a === 'string' ? scrubPlainString(a, level) : a,
      );
    }
  }

  // systemError sub-tree.
  const se = payload.systemError;
  if (se !== null && typeof se === 'object') {
    const sys = se as Record<string, unknown>;
    if (typeof sys.path === 'string') sys.path = scrubPlainString(sys.path, level);
    if (typeof sys.dest === 'string') sys.dest = scrubPlainString(sys.dest, level);
  }

  // Recursive cause chain.
  const cause = payload.cause;
  if (cause !== null && typeof cause === 'object') {
    scrubSerialisedErrorInPlace(cause as Record<string, unknown>, depth + 1, level);
  }
}

/**
 * Scrub infrastructure URLs from an error message that may be in
 * one of two shapes:
 *
 *   - Plain text — scrubbed in place.
 *   - Structured `[CRUCIBLE_ERR_V1]<json>` payload — JSON-decode,
 *     recursively scrub every user-facing string field
 *     (`message`, `recovery`, processOutput tails / argv / cwd /
 *     binary, systemError path / dest, recursive `cause`), re-
 *     encode. Codex round-2 finding: scrubbing only `.message`
 *     left URLs reachable via the other fields.
 *
 * If the JSON payload is corrupt, fall back to plain-string
 * scrubbing of the entire input. The chat reducer's
 * `unflattenIpcError` is already corruption-tolerant (returns a
 * synthesised error rather than throwing).
 */
/**
 * @param level — `'aggressive'` strips every `http://` / `https://`
 *   URL wholesale (use for Furnace-routed turns). `'banned-only'`
 *   strips only the explicit banned infrastructure substrings
 *   (use for local-build / flag-OFF paths so a benign
 *   `http://localhost:3000` error stays visible — Codex round-3
 *   flag-OFF regression). Defaults to `'aggressive'`.
 */
export function scrubInfrastructureUrls(text: string, level: ScrubLevel = 'aggressive'): string {
  if (!text) return text;
  // Structured-payload path. The sentinel can appear anywhere in
  // the text (e.g. Electron wraps it: `Error invoking remote
  // method 'channel': Error: [CRUCIBLE_ERR_V1]<json>`).
  const sentinelIdx = text.indexOf(STRUCTURED_IPC_ERROR_SENTINEL);
  if (sentinelIdx >= 0) {
    const prefix = text.slice(0, sentinelIdx);
    const json = text.slice(sentinelIdx + STRUCTURED_IPC_ERROR_SENTINEL.length);
    try {
      const parsed = JSON.parse(json) as Record<string, unknown>;
      scrubSerialisedErrorInPlace(parsed, 0, level);
      // Re-encode. The prefix scrub keeps any wrapper text
      // (Electron's `Error invoking remote method ...`)
      // honest too.
      return (
        scrubPlainString(prefix, level) + STRUCTURED_IPC_ERROR_SENTINEL + JSON.stringify(parsed)
      );
    } catch {
      // Fall through to plain scrub on corrupt JSON.
    }
  }
  return scrubPlainString(text, level);
}

/**
 * Renderer-side scrub for an already-decoded `SerialisedError`
 * shape. Used by call sites that have already called
 * `unflattenIpcError` and need to pass the cleaned shape into a
 * UI component (the chat bubble, the kickoff-failure detail line)
 * without re-encoding through the structured wire format.
 *
 * Returns a new object; the input is not mutated. Recursively
 * scrubs every user-facing string field including the `cause`
 * chain (per Codex round-2 follow-up).
 *
 * Generic `T` so the caller's specific shape (e.g.
 * `SerialisedError`) is preserved at the type level — the
 * scrubber is shape-tolerant (any JSON-compatible object works).
 */
export function scrubSerialisedError<T extends object>(
  payload: T,
  level: ScrubLevel = 'aggressive',
): T {
  // Deep-clone via JSON round-trip — the SerialisedError shape is
  // pure JSON-compatible by construction so the round-trip is
  // lossless. Then scrub in place.
  const cloned = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>;
  scrubSerialisedErrorInPlace(cloned, 0, level);
  return cloned as unknown as T;
}
